import { useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { Helmet } from "react-helmet";
import { Col, Row, Space, Form, Input, Button, Alert, Spin } from "antd";
import PageLayout from "../page-layout";
import AuthHelper from "components/auth/helpers";
import { emailRule, requiredRule } from "utils/validators/rules";
import { AUTH_STATUS } from "utils/Constants";
import { loginApi } from "ApiClient";
import { ApiURL, AppURL } from "utils/Actions";
import { initTimer, resetTimer } from "redux/reducers/LoginTimeOutSlice";
import { handleRedirect } from "utils/hooks/handleRedirect";
import { showSuccessNotification } from "utils/hooks/handleNotification";
import { setAfterTransferOwnership, setEnv } from "redux/reducers/GlobalUiSlice";
import style from "./style.module.css";
import { loginComponent } from "utils/AssetsData";
import { Env } from "utils/Types";

const defaultValues = { username: "", password: "" };

const DEFAULT_ERROR_MSG = "Could not login. Please try again.";

const Login: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  const [form] = Form.useForm();
  const [isLoggingIn, setIsLoggingIn] = useState<boolean>(false);
  const [isAuthFailed, setIsAuthFailed] = useState<boolean>(false);
  const [authFailedMsg, setAuthFailedMsg] = useState<string>("");
  const [hasWhiteLabel, setHasWhiteLabel] = useState<boolean>(false);
  const { afterTransferOwnership, successNotificationMsg } = useAppSelector(
    state => state.globalUI
  );
  const { whiteLabel = "", loading } = useAppSelector(state => state.properties);

  const locationState = history.location.state as any;

  useEffect(() => {
    setHasWhiteLabel(!loading && whiteLabel?.length > 0);
  }, [loading, whiteLabel]);

  useEffect(() => {
    if (locationState?.disableTimeoutPrompt) {
      AuthHelper.clearAuth();
      return;
    }
    handleRedirect(history, location.pathname);
  }, []);

  useEffect(() => {
    form.setFieldsValue(defaultValues);
    if (afterTransferOwnership) {
      showSuccessNotification({
        message: successNotificationMsg || ""
      });
    }
    dispatch(
      setAfterTransferOwnership({
        msg: undefined,
        afterTransferOwnership: false
      })
    );
  }, []);

  const handleAuthFailed = (error?: any) => {
    if (error) {
      console.error(error);
      AuthHelper.clearAuth();
      setIsAuthFailed(true);
    } else {
      console.error();
      AuthHelper.clearAuth();
    }
  };

  const onFinish = () => {
    setIsLoggingIn(true);
    form
      .validateFields()
      .then(async values => {
        const loginObj = {
          ...values,
          type: AUTH_STATUS.SCHOOL
        };
        try {
          const res = await loginApi.post(ApiURL.login, loginObj);
          const data = await res?.data;
          const attributes = data.attributes;

          if (attributes) {
            setIsAuthFailed(false);
            AuthHelper.setAuth(data);
            dispatch(initTimer(data.expireTime));
            // on login, reset to the common domestic environment
            dispatch(setEnv(Env.DOMESTIC));
            dispatch(resetTimer());
            const errorMessage = handleRedirect(history);
            if (errorMessage) {
              setIsAuthFailed(true);
              setAuthFailedMsg(errorMessage);
            }
          } else {
            return false;
          }
        } catch (error) {
          if (axios.isAxiosError(error)) {
            const errMsg = error.response?.data.message || DEFAULT_ERROR_MSG;
            setAuthFailedMsg(errMsg);
          }
          handleAuthFailed(error);
          return false;
        }
      })
      .catch(error => {
        setAuthFailedMsg(DEFAULT_ERROR_MSG);
        handleAuthFailed(error);
        return false;
      })
      .finally(() => {
        setIsLoggingIn(false);
      });
  };

  return (
    <>
      <Helmet>
        <title>{t("helmet.login.title")}</title>
        <meta name="description" content={t("helmet.login.meta") || ""} />
      </Helmet>

      <PageLayout isSingleCard={false}>
        {hasWhiteLabel ? (
          <Row style={{ width: "100%", height: "100%" }}>
            <Col
              xs={24}
              sm={12}
              className={[style["first-column"], style["column-inner"]].join(" ")}
            >
              <h1 className="text-center">{t("login.title")}</h1>
              {isAuthFailed && (
                <Alert
                  message={authFailedMsg}
                  type="error"
                  closable
                  showIcon
                  style={{ margin: "20px 0" }}
                  onClose={() => {
                    setIsAuthFailed(false);
                  }}
                />
              )}
              <Form
                layout="vertical"
                form={form}
                onFinish={onFinish}
                requiredMark={false}
                initialValues={defaultValues}
                disabled={isLoggingIn}
              >
                <Form.Item
                  required
                  label={t("login.usenameLabel")}
                  name="username"
                  validateTrigger="onBlur"
                  rules={[requiredRule, emailRule]}
                >
                  <Input autoFocus />
                </Form.Item>

                <Form.Item
                  required
                  label={t("login.passwordLabel")}
                  name="password"
                  rules={[requiredRule]}
                >
                  <Input.Password />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={isLoggingIn}
                    block
                    style={{ marginTop: "20px" }}
                  >
                    {t("login.loginBtn")}
                  </Button>
                </Form.Item>

                <Button
                  block
                  type="link"
                  onClick={() => {
                    history.push(AppURL.forgetPassword);
                  }}
                >
                  {t("login.forgetpasswordLink")}
                </Button>
              </Form>
            </Col>
            <Col xs={0} sm={12} className={style["column-inner-right"]}>
              <Space>{hasWhiteLabel ? loginComponent[whiteLabel].right : <></>}</Space>
            </Col>
          </Row>
        ) : (
          <div className="center-content" style={{ padding: 40 }}>
            <Spin size="large" />
          </div>
        )}
      </PageLayout>
    </>
  );
};

export default Login;
